import { useState } from 'react';
import { Badge, Button } from '../design-system/components';

/**
 * Timeline slot component
 * Shows available/booked time slots with special highlighting
 */
export const TimelineSlot = ({
  slot,
  isPreferredTime,
  isAdjacentTime,
  isStacking,
  isBooked,
  assignedRequest,
  selectedRequest,
  onAssign,
  onChangeField,
  onDragOver,
  onDragLeave,
  onDrop,
  onCancelBooking
}) => {
  const [showCancelPopover, setShowCancelPopover] = useState(false);

  const handleClick = () => {
    if (isBooked) {
      setShowCancelPopover((prev) => !prev);
    } else if (selectedRequest) {
      if (selectedRequest['Request Status'] === 'Scheduled') {
        if (confirm(`Reassign from ${selectedRequest.SlotID} to this field?`)) {
          onChangeField(selectedRequest, slot);
        }
      } else {
        onAssign(selectedRequest, slot);
      }
    }
  };

  return (
    <div
      onDrop={(e) => !isBooked && onDrop(e, slot)}
      onDragOver={!isBooked ? onDragOver : null}
      onDragLeave={!isBooked ? onDragLeave : null}
      onClick={handleClick}
      className="timeline-slot"
      style={{
        border: `2px solid ${isBooked ? 'var(--border-strong)' : 'var(--success)'}`,
        background: isBooked ? 'var(--surface-sunken)' : 'var(--surface-card)',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--space-3)',
        fontFamily: 'var(--font-sans)',
        fontSize: 'var(--text-sm)',
        cursor: isBooked || selectedRequest ? 'pointer' : 'default',
      }}
    >
      <div className="flex justify-between items-start">
        <div className="flex-1">
          <div className="font-semibold flex items-center gap-2 flex-wrap">
            <span style={{ fontFamily: 'var(--font-mono)' }}>{slot.Time} - {slot['Duration (hours)']}hrs</span>
            <Badge tone="info" variant="soft">{slot['Field Size']}</Badge>
            {isBooked && assignedRequest?.['Game Number'] && (
              <span style={{ fontFamily: 'var(--font-mono)', background: 'var(--ink)', color: 'var(--text-inverse)', padding: '3px 8px', fontWeight: 'var(--fw-bold)', fontSize: 'var(--text-xs)', borderRadius: 'var(--radius-sm)' }}>
                #{assignedRequest['Game Number']}
              </span>
            )}
            {isPreferredTime && !isBooked && (
              <Badge tone="neutral" variant="solid" icon="target">Requested time</Badge>
            )}
            {isAdjacentTime && !isBooked && !isPreferredTime && (
              <Badge tone="info" variant="solid" icon="clock">Adjacent time</Badge>
            )}
            {isStacking && !isBooked && !isPreferredTime && !isAdjacentTime && (
              <Badge tone="success" variant="solid" icon="zap">Stack opportunity</Badge>
            )}
            {isPreferredTime && isStacking && !isBooked && (
              <Badge tone="warning" variant="solid" icon="target" className="animate-pulse">Bingo - requested time</Badge>
            )}
          </div>
          {isBooked && assignedRequest && (
            <div className="mt-1" style={{ fontSize: 'var(--text-xs)', color: 'var(--text-body)' }}>
              <div className="flex items-center gap-2 flex-wrap">
                <strong>{assignedRequest['Team Name']}</strong>
                {assignedRequest['Game Type'] && (
                  <Badge
                    tone={
                      assignedRequest['Game Type'] === 'Friendly' ? 'success' :
                      assignedRequest['Game Type'] === 'League' ? 'info' :
                      assignedRequest['Game Type'] === 'Tournament' ? 'warning' :
                      'neutral'
                    }
                    variant="soft"
                  >
                    {assignedRequest['Game Type']}
                  </Badge>
                )}
                {assignedRequest['Contact Name'] && (
                  <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
                    &bull; {assignedRequest['Contact Name']}
                  </span>
                )}
              </div>
              <div>
                {assignedRequest['Home Team']} vs{' '}
                {assignedRequest['Away Team']}
              </div>
            </div>
          )}
          {!isBooked && (
            <div className="mt-1" style={{ fontSize: 'var(--text-xs)', color: 'var(--folk-green-deep)' }}>
              Available &mdash; Drop request here
            </div>
          )}
        </div>
        <Badge tone={isBooked ? 'danger' : 'success'} variant="soft" dot>
          {isBooked ? 'Booked' : 'Open'}
        </Badge>
      </div>

      {/* Cancel Booking popover — shown when admin clicks a booked slot */}
      {isBooked && showCancelPopover && assignedRequest && (
        <div
          className="mt-3"
          onClick={(e) => e.stopPropagation()}
          style={{ padding: 'var(--space-3)', background: 'var(--danger-soft)', border: '2px solid var(--danger)', borderRadius: 'var(--radius-md)' }}
        >
          <div className="mb-2" style={{ fontFamily: 'var(--font-sans)', fontWeight: 'var(--fw-bold)', fontSize: 'var(--text-xs)', letterSpacing: 'var(--ls-wide)', color: 'var(--dala-red-deep)' }}>
            Cancel this booking?
          </div>
          <div className="mb-3 space-y-0.5" style={{ fontSize: 'var(--text-xs)', color: 'var(--text-body)' }}>
            <div><strong>Team:</strong> {assignedRequest['Team Name']}</div>
            {assignedRequest['Game Type'] && (
              <div><strong>Type:</strong> {assignedRequest['Game Type']}</div>
            )}
            {assignedRequest['Game Number'] && (
              <div><strong>Game #:</strong> {assignedRequest['Game Number']}</div>
            )}
            <div>
              <strong>Match:</strong> {assignedRequest['Home Team']} vs{' '}
              {assignedRequest['Away Team']}
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              block
              variant="danger"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                const reason = prompt('Cancellation reason (required):');
                if (reason !== null && reason.trim() !== '') {
                  onCancelBooking(assignedRequest, reason.trim());
                  setShowCancelPopover(false);
                }
              }}
            >
              Cancel Booking
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                setShowCancelPopover(false);
              }}
            >
              Dismiss
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
